import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import LoadFailed from '../../teacher_payments/shared/load_failed';
import ReviewPill from '../../teacher_payments/penalties/review_pill';
import { fetch_penalties_session, post_issue_action } from '../../teacher_payments/api';
import { is_permission_error } from '../../teacher_payments/format';
import { session_params } from '../../teacher_payments/penalties/requests';
import ChangePenaltyModal from './change_penalty_modal';
import { session_view } from './adapters';
import '../../teacher_payments/teacher_payments.css';
import '../../teacher_payments/penalties/penalties.css';
import './payment_penalties.css';

/*
 * PROD-7828 R6 — the teacher-payment penalties strip on a session page: the D90 review pill and the
 * Change penalty modal, both on GET /penalties/session. Every route is grant-gated, so a 401/403 hides
 * the strip for Ops without payment access (the teacher profile's payment card does the same); a 404
 * hides it too, since a server image without the module answers every route that way (CD-224).
 */
export const LOAD_FAILED_TEXT = "Could not load this session's teacher payment penalties.";

const hides_strip = (e) =>
  is_permission_error(e) || (!!e && !!e.response && e.response.status === 404);

const SessionPaymentPenalties = ({
  session_uuid,
  teacher_uuid,
  teacher_name,
  new_request_id = uuidv4
}) => {
  const [state, setState] = useState({ status: 'loading', view: null });
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);

  const hide = useCallback(() => {
    setOpen(false);
    setHidden(true);
  }, []);

  // keep: a re-read after a write leaves the rows on screen until the new ones land. If it fails, the
  // view stays only as the open modal's context; its rows are never offered again as current.
  const load = useCallback(
    (keep = false) => {
      seq.current += 1;
      const mine = seq.current;
      if (!keep) setState({ status: 'loading', view: null });
      fetch_penalties_session(session_params(session_uuid))
        .then((data) => {
          if (mine === seq.current) setState({ status: 'ok', view: session_view(data) });
        })
        .catch((e) => {
          if (mine !== seq.current) return;
          if (hides_strip(e)) {
            hide();
            return;
          }
          console.error('payment penalties: session load failed', session_uuid, e);
          setState((s) => ({ status: 'error', view: keep ? s.view : null }));
        });
    },
    [session_uuid, hide]
  );

  useEffect(() => {
    setHidden(false);
    setOpen(false);
    if (session_uuid) load();
  }, [session_uuid, load]);

  if (!session_uuid || hidden) return null;
  const { status, view } = state;
  const reload = () => load(true);

  return (
    <div className="tp-sp">
      <span className="tp-sp-title">Teacher payment penalties</span>
      {status === 'loading' ? <span className="tp-card-field-label">Loading…</span> : null}
      {status === 'error' ? <LoadFailed text={LOAD_FAILED_TEXT} on_retry={() => load()} /> : null}
      {status === 'ok' && view ? (
        <>
          <span className="tp-card-field-label">
            {view.rows.length
              ? `${view.rows.length} on this session · charged ${view.charged_text}`
              : 'None on this session'}
          </span>
          <ReviewPill
            issues={view.issues}
            teacher_names={view.teacher_names}
            post_action={post_issue_action}
            on_changed={reload}
            on_permission={hide}
          />
          <button type="button" className="tp-btn-out" onClick={() => setOpen(true)}>
            Change penalty
          </button>
        </>
      ) : null}
      {open && view ? (
        <ChangePenaltyModal
          view={view}
          teacher_uuid={teacher_uuid}
          teacher_name={teacher_name}
          new_request_id={new_request_id}
          load_failed_text={status === 'error' ? LOAD_FAILED_TEXT : ''}
          on_reload={reload}
          on_permission={hide}
          on_close={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default SessionPaymentPenalties;
